import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, Button, LoadingSpinner } from '../components';
import { useUser, useAuth } from '../context';

/**
 * Subscription Page - Party Resume Pro
 * Upgrade to premium features
 */
export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const plans = {
    monthly: {
      price: 9.99,
      period: 'month',
      savings: null,
    },
    annual: {
      price: 79.99,
      period: 'year',
      savings: 'Save 33%',
    },
  };

  const features = [
    {
      icon: '✓',
      title: 'Verified Badge',
      description: 'Stand out with a verified checkmark on your Party Resume',
    },
    {
      icon: '★',
      title: 'Priority Review',
      description: 'Your applications appear first in host review queues',
    },
    {
      icon: '📸',
      title: 'Extended Gallery',
      description: 'Upload up to 12 photos (vs 6 for free accounts)',
    },
    {
      icon: '📊',
      title: 'Host Analytics',
      description: 'See detailed stats on your events and guest engagement',
    },
    {
      icon: '💰',
      title: 'Reduced Fees',
      description: '2.5% platform fee instead of 5% on ticket sales',
    },
    {
      icon: '🎟️',
      title: 'Golden Tickets',
      description: '2 free Golden Tickets per month to skip application queues',
    },
  ];

  const handleSubscribe = async () => {
    setIsLoading(true);
    // TODO: Integrate with Stripe Checkout for subscriptions
    setTimeout(() => {
      setIsLoading(false);
      // For now, show coming soon message
      alert('Subscriptions coming soon! Stay tuned.');
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/')}
      />

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-4">
            PARTY RESUME PRO
          </h1>
          <p className="font-mono text-sm text-gray-600 max-w-lg mx-auto">
            Unlock premium features to enhance your party experience
          </p>
        </div>

        {/* Plan Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex border-2 border-black">
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`
                px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider
                transition-colors duration-200
                ${selectedPlan === 'monthly'
                  ? 'bg-black text-white'
                  : 'bg-white text-black hover:bg-gray-100'
                }
              `}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedPlan('annual')}
              className={`
                px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider
                border-l-2 border-black
                transition-colors duration-200
                ${selectedPlan === 'annual'
                  ? 'bg-black text-white'
                  : 'bg-white text-black hover:bg-gray-100'
                }
              `}
            >
              Annual
              {plans.annual.savings && (
                <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs">
                  {plans.annual.savings}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto mb-12">
          <div className="border-4 border-black bg-white p-8">
            <div className="text-center mb-8">
              <div className="font-mono text-5xl font-bold text-black">
                ${plans[selectedPlan].price}
              </div>
              <div className="font-mono text-sm text-gray-500 uppercase tracking-wider mt-2">
                per {plans[selectedPlan].period}
              </div>
            </div>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleSubscribe}
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'UPGRADE NOW'}
            </Button>

            <p className="font-mono text-xs text-gray-500 text-center mt-4">
              Cancel anytime. No commitment.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-12">
          <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black text-center mb-8">
            WHAT YOU GET
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="border-2 border-black bg-white p-6"
              >
                <div className="text-2xl mb-3">{feature.icon}</div>
                <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-2">
                  {feature.title}
                </h3>
                <p className="font-mono text-xs text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="border-2 border-black bg-white overflow-hidden">
          <div className="grid grid-cols-3 border-b-2 border-black bg-gray-50">
            <div className="p-4 border-r-2 border-black">
              <span className="font-mono text-sm font-bold uppercase tracking-wider text-gray-500">
                Feature
              </span>
            </div>
            <div className="p-4 border-r-2 border-black text-center">
              <span className="font-mono text-sm font-bold uppercase tracking-wider text-gray-500">
                Free
              </span>
            </div>
            <div className="p-4 text-center bg-black">
              <span className="font-mono text-sm font-bold uppercase tracking-wider text-white">
                Pro
              </span>
            </div>
          </div>

          {[
            { feature: 'Gallery Photos', free: '6', pro: '12' },
            { feature: 'Platform Fee', free: '5%', pro: '2.5%' },
            { feature: 'Verified Badge', free: '—', pro: '✓' },
            { feature: 'Priority Review', free: '—', pro: '✓' },
            { feature: 'Host Analytics', free: 'Basic', pro: 'Advanced' },
            { feature: 'Golden Tickets', free: '—', pro: '2/month' },
            { feature: 'Support', free: 'Email', pro: 'Priority' },
          ].map((row, index) => (
            <div
              key={index}
              className={`grid grid-cols-3 ${index !== 6 ? 'border-b-2 border-black' : ''}`}
            >
              <div className="p-4 border-r-2 border-black">
                <span className="font-mono text-sm text-black">
                  {row.feature}
                </span>
              </div>
              <div className="p-4 border-r-2 border-black text-center">
                <span className="font-mono text-sm text-gray-500">
                  {row.free}
                </span>
              </div>
              <div className="p-4 text-center">
                <span className="font-mono text-sm font-bold text-black">
                  {row.pro}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-12">
          <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black text-center mb-8">
            QUESTIONS?
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time. Your Pro benefits will continue until the end of your billing period.',
              },
              {
                q: 'Do I need Pro to use Party Resume?',
                a: 'No. The free tier includes all core features. Pro just enhances your experience with extra perks.',
              },
              {
                q: 'What happens to my photos if I downgrade?',
                a: 'Your photos stay safe. You just won\'t be able to add new ones until you\'re under the free limit.',
              },
            ].map((faq, index) => (
              <div key={index} className="border-2 border-black bg-white p-6">
                <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-2">
                  {faq.q}
                </h3>
                <p className="font-mono text-sm text-gray-600">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-sm text-gray-500 uppercase tracking-wider hover:text-black transition-colors"
          >
            ← Back
          </button>
        </div>
      </main>
    </div>
  );
}
